/**
 * Pure semantic color mapping for the design-system restyle.
 *
 * These helpers map semantic names (scan status, capability presence) to the
 * theme's CSS custom-property references declared in `theme.css`. They are pure
 * — no DOM, no side effects — so they are trivially property-testable.
 *
 * Consumers reference the returned `var(--…)` string directly in inline styles
 * or as a CSS custom-property value; the actual color resolves from the token
 * declared on `:root` in `theme.css`.
 */

import type { ScanStatus } from "../types";

/**
 * Maps a terminal scan status to its Status_Pill color token (Req 25.1–25.3):
 * - `success` → `var(--green-500)`  (Green ramp)
 * - `partial` → `var(--orange-500)` (Orange ramp)
 * - `failed`  → `var(--danger-red)` (Danger_Red)
 *
 * The three statuses map to three distinct token references.
 */
export function statusPillColor(status: ScanStatus): string {
  switch (status) {
    case "success":
      return "var(--green-500)";
    case "partial":
      return "var(--orange-500)";
    case "failed":
      return "var(--danger-red)";
  }
}

/**
 * Maps capability presence to its Capability_Indicator color token (Req 25.4):
 * - present (`true`)  → `var(--green-500)`   (Green ramp)
 * - absent  (`false`) → `var(--neutral-500)` (muted Neutral ramp)
 */
export function capabilityColor(present: boolean): string {
  return present ? "var(--green-500)" : "var(--neutral-500)";
}
